import React from 'react';
import cn from '@/utils/cn';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPAButton from '../shared/SPAButton';
import SPADataTable from '../shared/SPADataTable';
import SPAPageHeader from '../shared/SPAPageHeader';
import ReturnToTeacher from './ReturnToTeacher';
import SPAPill from '../shared/SPAPill';
import SPASectionHeading from '../shared/SPASectionHeading';
import { useSPAUser } from '../shared/SPAUser';
import useAssignmentSyncState from './useAssignmentSyncState';
import type { StudentSyncRow, AssignmentSummary, LaunchesPayload } from './useAssignmentSyncState';
import { REASON_LABELS, STATE_LABELS, STATE_TONES } from './syncStates';
import ClassLaunches from './ClassLaunches';
import CopiesPanel from './CopiesPanel';
import CopySummaryButton from './CopySummaryButton';
import FixCard from './FixCard';
import VerifyPanel from './VerifyPanel';
import RosterPanel from './RosterPanel';
import SectionTabs from './SectionTabs';
import type { SectionTab } from './SectionTabs';
import { GradeSyncLink, linkClass } from './gradeSyncPath';

const column = createColumnHelper<StudentSyncRow>();

// Problems first: the student support was asked about is rarely in sync, and a zero for missing work
// is owed but never a grade to recover.
const SEVERITY: Record<StudentSyncRow['state'], number> = {
  in_sync: 3, never_synced: 0, no_score: 5, not_due: 4, out_of_sync: 1, retrying: 2,
};

const severity = (row: StudentSyncRow) => (row.zero_for_missing_work ? 6 : SEVERITY[row.state]);

const bySeverity = (rows: StudentSyncRow[]) => [...rows].sort((a, b) => severity(a) - severity(b));

// History is for anyone who can open this page; the raw log behind it is Sysadmin-only.
const historyColumn = column.display({
  cell: info => (
    <Link
      className={cn(linkClass, 'tw-whitespace-nowrap')}
      to={`history/${info.row.original.student_id}`}
    >
      History
    </Link>
  ),
  header: 'Sync history',
  id: 'history',
});

const columns = [
  column.accessor('student_name', { header: 'Student' }),
  column.accessor('state', {
    cell: info => (info.row.original.zero_for_missing_work
      ? <SPAPill tone="neutral">Zero for missing work</SPAPill>
      : <SPAPill tone={STATE_TONES[info.getValue()]}>{STATE_LABELS[info.getValue()]}</SPAPill>),
    header: 'State',
  }),
  column.accessor('current_score', {
    cell: info => info.getValue() ?? '—',
    header: 'Score in TCI',
    meta: { align: 'right' },
  }),
  column.accessor('synced_score', {
    cell: info => info.getValue() ?? '—',
    header: 'Score in LMS',
    meta: { align: 'right' },
  }),
  // An unrecognised failure shows the LMS's own message; a known one keeps it on hover.
  column.accessor('reason', {
    cell: (info) => {
      const reason = info.getValue();
      const detail = info.row.original.reason_detail;

      if (!reason) return '—';

      return (
        <span title={detail ?? undefined}>
          {REASON_LABELS[reason] ?? reason}
          {reason === 'unknown' && detail != null && (
            <span className="tw-block tw-break-words tw-text-sm tw-text-spa-secondary">{detail}</span>
          )}
        </span>
      );
    },
    header: 'Why not',
  }),
  // AIDEV-NOTE: The distinction support keeps having to work out by hand — a student who never
  // launched cannot open the assignment at all, while one in no linked course can work normally and
  // still never have a score delivered. Both look like "not synced" without this.
  column.accessor('lms', {
    cell: (info) => {
      const reach = info.getValue();

      if (reach?.status === 'reachable') {
        const where = `in course ${(reach.courses ?? []).join(', ')}`;

        // In the course, but Canvas has the assignment assigned to others only: every score is refused.
        if (reach.assigned_in_lms === false) {
          return (
            <span>
              <SPAPill tone="error">Not assigned in Canvas</SPAPill>
              <span className="tw-block tw-text-sm tw-text-spa-secondary">{where}</span>
            </span>
          );
        }

        return <span className="tw-text-spa-secondary">{where}</span>;
      }
      if (reach?.status === 'no_course') {
        return <SPAPill tone="error">In no linked course</SPAPill>;
      }
      if (reach?.status === 'never_launched') {
        return <SPAPill tone="warning">Never launched</SPAPill>;
      }

      return '—';
    },
    header: 'In the LMS',
  }),
  // Opened *this* assignment, unlike "In the LMS", which only says whether they ever launched anything.
  column.accessor(row => row.opened?.last_at ?? '', {
    cell: (info) => {
      const { opened } = info.row.original;

      if (opened == null) return <span className="tw-text-spa-tertiary">Never</span>;

      return (
        <span>
          {new Date(opened.last_at).toLocaleDateString()}
          {opened.course_title != null && (
            <span className="tw-block tw-text-sm tw-text-spa-secondary">{opened.course_title}</span>
          )}
        </span>
      );
    },
    header: 'Opened from LMS',
    id: 'opened',
  }),
  column.accessor('synced_at', {
    cell: info => (info.getValue() ? new Date(info.getValue() as string).toLocaleString() : '—'),
    header: 'Last delivered',
  }),
  historyColumn,
] as ColumnDef<StudentSyncRow, unknown>[];

// Whether the teacher has opened this assignment from the LMS: the launch that links it, and the first
// thing to rule out when it never was.
const TeacherOpens = ({ opens, staffer }: { opens: LaunchesPayload['teacher']; staffer: string | null }) => {
  const who = staffer ?? 'The teacher';
  const [latest] = opens;

  return (
    <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">
      {latest == null
        ? `${who} has never opened this assignment from the LMS.`
        : `${who} last opened it from ${latest.course_title ?? `LMS course ${latest.course_id}`} on `
          + `${new Date(latest.at).toLocaleDateString()}${opens.length > 1 ? ` (${opens.length} recent opens)` : ''}.`}
    </p>
  );
};

// The facts the old three-lane summary spread over two cards, on one line under the title.
interface FactsProps { assignment: AssignmentSummary; delivered: number; owed: number; spare: boolean }

// Zeros for missing work are left to the check panel, which counts every assignee; this list stops at 200.
const Facts = ({
  assignment, delivered, owed, spare,
}: FactsProps) => (
  <p className="tw-mb-5 tw-mt-0 tw-text-sm tw-text-spa-secondary">
    {assignment.staffer_id == null
      ? (assignment.staffer ?? 'No teacher')
      : (
        <GradeSyncLink
          to={`/teachers/${assignment.staffer_id}`}
        >
          {assignment.staffer ?? assignment.staffer_id}
        </GradeSyncLink>
      )}
    {` · ${assignment.classroom_count} ${assignment.classroom_count === 1 ? 'class' : 'classes'}`}
    {` · ${assignment.student_count} students`}
    {` · ${assignment.lms_type ?? 'no LMS'}`}
    {assignment.courses.length > 0 ? ` · courses ${assignment.courses.join(', ')}` : ' · not linked to a course'}
    {owed > 0 && (spare
      ? ` · ${owed} scores, sent by the copies for its classes`
      : ` · ${delivered} of ${owed} scores delivered`)}
    <span className="tw-text-spa-tertiary">{` · assignment ${assignment.id}`}</span>
  </p>
);

const AssignmentDetail = () => {
  const navigate = useNavigate();
  const { canViewSyncLog } = useSPAUser();
  const { id: assignmentId = '' } = useParams();
  const { data, isFetching, error } = useAssignmentSyncState(assignmentId);
  // A repair lands here with ?verify=1, so checking that it worked is the next step, not a hunt.
  const [search] = useSearchParams();

  const assignment = data?.assignment;
  const launches = data?.launches;
  const launchCourses = new Map((launches?.classes ?? []).map(room => [room.id, room.courses]));
  const showRoster = (assignment?.courses.length ?? 0) > 0;
  // The class map carries launches as a column; when it can't render, they show on their own.
  const showClassMap = showRoster && data?.roster != null && data.roster.error == null;
  const students = bySeverity(data?.students ?? [])
    .map(student => ({ ...student, opened: launches?.students[String(student.student_id)] }));
  // Real scores only: a zero the scorer supplies for missing work is owed but not a grade to recover.
  const earned = students.filter(s => !s.zero_for_missing_work);
  const delivered = earned.filter(s => s.state === 'in_sync').length;
  // Students with nothing to deliver are not a health signal in either direction.
  const owed = earned.filter(s => s.state !== 'no_score' && s.state !== 'not_due').length;
  // Another copy delivers every score: its own link checks and re-send would only argue with that.
  const spare = (data?.diagnostics ?? []).some(finding => finding.code === 'covered_by_copies');
  const verifying = search.get('verify') === '1';

  const copies = data?.copies?.copies ?? [];
  const neverLinked = copies.filter(copy => !copy.linked).length;
  const tabs: SectionTab[] = [
    { key: 'students', label: `Students (${assignment?.student_count ?? students.length})` },
  ];
  if (showRoster || (launches?.classes.length ?? 0) > 0) tabs.push({ key: 'classes', label: 'Classes' });
  if (copies.length > 1) {
    tabs.push({
      alert: neverLinked > 0 ? `${neverLinked} never linked` : null,
      key: 'copies',
      label: `Copies (${copies.length})`,
    });
  }
  const section = tabs.find(tab => tab.key === search.get('tab'))?.key ?? 'students';

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        {/* AIDEV-NOTE: Was `to="../.."`, which climbed two Routes and landed on /sysadmin — out of
            grade sync altogether — under a label promising a teacher page that did not exist yet. It
            does now, and the assignment knows its staffer, so the link goes where it says. */}
        <GradeSyncLink
          className="tw-mb-2 tw-inline-block tw-text-sm"
          to={assignment?.staffer_id == null
            ? '/troubleshoot'
            : `/teachers/${assignment.staffer_id}`}
        >
          {assignment?.staffer == null
            ? '← Back to grade sync'
            : `← All of ${assignment.staffer}'s assignments`}
        </GradeSyncLink>
        <SPAPageHeader
          actions={assignment && (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              {data != null && <CopySummaryButton payload={data} />}
              {canViewSyncLog && (
                <SPAButton size="xs" type="button" variant="secondary" onClick={() => navigate('log')}>
                  Sync log
                </SPAButton>
              )}
              <SPAButton size="xs" type="button" variant="secondary" onClick={() => navigate('columns')}>
                All gradebook columns
              </SPAButton>
              {assignment.staffer_id != null && (
                <ReturnToTeacher name={assignment.staffer} stafferId={assignment.staffer_id} />
              )}
            </div>
          )}
          title={assignment?.title ?? 'Assignment'}
        />

        {error != null && (
          <p className="tw-m-0 tw-text-sm tw-text-spa-error">
            Couldn&apos;t load that assignment. Check the ID and try again.
          </p>
        )}

        {assignment && (
          <>
            <Facts assignment={assignment} delivered={delivered} owed={owed} spare={spare} />
            {launches != null && assignment.lms_type === 'LTI' && (
              <TeacherOpens opens={launches.teacher} staffer={assignment.staffer} />
            )}

            {/* Diagnosis first; after a repair (?verify=1) the check that it worked comes first instead. */}
            {verifying && !spare && <VerifyPanel focus assignmentId={assignmentId} />}
            <FixCard
              assignment={assignment}
              assignmentId={assignmentId}
              findings={data?.diagnostics ?? []}
              neverLaunched={data?.roster?.unreachable?.never_launched ?? 0}
            />
            {!verifying && !spare && <VerifyPanel assignmentId={assignmentId} focus={false} />}

            <SectionTabs active={section} tabs={tabs} />

            {/* Unlinked, every class reads "no course found" — the card above already says why. */}
            {section === 'classes' && showRoster && (
              <div className="tw-mb-6">
                <SPASectionHeading title="Who it's assigned to" />
                <RosterPanel
                  assignmentId={assignmentId}
                  data={data?.roster}
                  launchCourses={launchCourses}
                  onSelectCourse={id => navigate(`courses/${id}?tab=classes`)}
                />
              </div>
            )}

            {section === 'classes' && !showClassMap && (launches?.classes.length ?? 0) > 0 && (
              <ClassLaunches classes={launches?.classes ?? []} />
            )}

            {section === 'copies' && (
              <CopiesPanel copies={copies} unopened={data?.copies?.unopened_classes ?? []} />
            )}
          </>
        )}

        {/* Routed dialogs: /columns, /courses/:courseId, /log and /history/:studentId render here. */}
        <Outlet />

        {assignmentId !== '' && section === 'students' && (
          <SPADataTable
            columns={columns}
            data={students}
            defaultSort={[]}
            emptyMessage="No students are assigned to this assignment."
            footnote={(
              <>
                &ldquo;Score in LMS&rdquo; is the value we last delivered successfully. Neither
                Canvas nor Schoology reports the stored score back, so it is not read from the
                gradebook.
              </>
            )}
            isLoading={isFetching}
          />
        )}
      </div>
    </div>
  );
};

export default AssignmentDetail;
